import { Locale } from "@/Common/Models/Localization/Locale";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { OntarioObstetricalVisit } from "@/Patient/Models/ObstetricalFile/Ontario/OPR3/OntarioObstetricalVisit";
import { IObstetricalFilePageProps, ObstetricalFilePage, ObstetricalFilePageState } from "../ObstetricalFilePage";
import { BaseOntarioObstetricalFileSubSectionProps } from "./Subcomponent/BaseOntarioObstetricalFileSubSection";

abstract class OntarioObstetricalFilePageState extends ObstetricalFilePageState<OntarioObstetricalFileForm> {
}

abstract class OntarioObstetricalFilePage<TState extends OntarioObstetricalFilePageState> extends ObstetricalFilePage<OntarioObstetricalFileForm, TState> {
    private _vitalSignControlRenderer?: ObstetricalFileControlRenderer = null;
    protected get locale(): Locale { return Locale.en; }

    constructor(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>) {
        super(props);
        this.setVitalSignControlRenderer(props.readonly);
    }

    protected get vitalSignControlRenderer(): ObstetricalFileControlRenderer {
        if (this._vitalSignControlRenderer?.getIsReadonly() !== this.props.readonly) {
            this.setVitalSignControlRenderer(this.props.readonly);
        }
        return this._vitalSignControlRenderer;
    }

    protected setVitalSignControlRenderer(readonly: boolean): void {
        this._vitalSignControlRenderer = new ObstetricalFileControlRenderer({
            readonly: readonly,
            onBlur: () => this.onVitalSignUpdated(),
            locale: this.locale
        });
    }

    protected onVitalSignUpdated(): void {
        throw new Error("The vital sign control renderer callback has not been implemented for the current page.");
    }

    protected buildState(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>): TState {
        const state = {
            form: new OntarioObstetricalFileForm(props.form)
        } as TState;

        return state;
    }

    protected getString(key: string): string {
        return cacheStore.GetString(key, null, this.locale);
    }

    protected renderFirstName(colClassName: string): JSX.Element {
        return this.renderField(
            "FirstNameLabel",
            <span className="field-text">{this.props.patient.IndividualResponse.FirstName?.substr(0, OntarioObstetricalFileFieldLimits.Page1.PatientLastName.MaxChars)}</span>,
            colClassName,
            null,
            this.locale
        );
    }

    protected renderLastName(colClassName: string): JSX.Element {
        return this.renderField(
            "LastNameLabel",
            <span className="field-text">{this.props.patient.IndividualResponse.LastName?.substr(0, OntarioObstetricalFileFieldLimits.Page1.PatientFirstName.MaxChars)}</span>,
            colClassName,
            null,
            this.locale
        );
    }

    protected renderFinalEstimatedBirthDate(id: string, colClassName: string): JSX.Element {
        return this.renderField(
            "ObstetricalFile_FinalEdb",
            this.controlRenderer.renderDateSelector({
                value: this.state.form.Common?.FinalEstimatedDateOfBirth,
                onChange: value => this.mutateModelStateWithFormUpdate(
                    s => {
                        s.form.Common.FinalEstimatedDateOfBirth = value;
                        s.form.Opr2.Ultrasound.mutate(p => p.UseForDating = false);

                        const finalEdb = LuxonDateTimeParser.parse(value);
                        const dateOfBirth = LuxonDateTimeParser.parse(this.props.patient.IndividualResponse.DateOfBirth);

                        const ageAtEstimatedDateOfBirth = finalEdb && dateOfBirth
                            ? Math.floor(finalEdb.diff(dateOfBirth, "year")?.years)
                            : null;
                        s.form.Opr1.PersonalInformation.AgeAtEstimatedDateOfBirth = ageAtEstimatedDateOfBirth;

                        s.form.updateSubsequentVisitGestationalAge(this.props.contributionId);
                        s.form.updateGestationalAgeAtBirth();
                    }
                ),
                id
            }),
            colClassName
        );
    }

    protected renderNewBornFields(colClassName: string): JSX.Element {
        return this.renderField(
            "ObstetricalFile_NewbornCareProviderLabel",
            <div className="multiple-inline-fields">
                <label>{cacheStore.GetString("InHospitalLabel")}</label>
                <div className="inline-field-grow">
                    {this.controlRenderer.renderTextbox({
                        value: this.state.form.Common.NewbornCareProviderInHospital,
                        onChange: value => this.mutateModelState(s => s.form.Common.NewbornCareProviderInHospital = value),
                        maxLength: OntarioObstetricalFileFieldLimits.Page1.NewbornCareProviderInHospital.MaxChars
                    })}
                </div>
                <label>{cacheStore.GetString("InCommunityLabel")}</label>
                <div className="inline-field-grow">
                    {this.controlRenderer.renderTextbox({
                        value: this.state.form.Common.NewbornCareProviderInCommunity,
                        onChange: value => this.mutateModelState(s => s.form.Common.NewbornCareProviderInCommunity = value),
                        maxLength: OntarioObstetricalFileFieldLimits.Page1.NewbornCareProviderInCommunity.MaxChars
                    })}
                </div>
            </div>,
            colClassName
        );
    }

    protected renderPlannedBirthAttendant(colClassName: string): JSX.Element {
        return this.renderField(
            "ObstetricalFile_PlannedBirthAttendantLabel",
            this.controlRenderer.renderPhysicianSearchTextbox(
                this.state.form.Common.PlannedBirthAttendant,
                (attendant, isSelected) => this.mutateModelState(
                    s => s.form.Common.PlannedBirthAttendant = attendant,
                    () => {
                        if (isSelected)
                            this.handleFormUpdate();
                    }
                ),
                null,
                "ObstetricalFile_PlannedBirthAttendant",
                OntarioObstetricalFileFieldLimits.Page1.PlannedBirthAttendant.MaxChars
            ),
            colClassName
        );
    }

    protected renderFamilyPhysicianOrPrimaryCareProvider(colClassName: string): JSX.Element {
        return this.renderField(
            "ObstetricalFile_FamilyPhysicianOrPrimaryCareProviderLabel",
            this.controlRenderer.renderPhysicianSearchTextbox(
                this.state.form.Common.FamilyPhysicianPrimaryCareProvider,
                (attendant, isSelected) => this.mutateModelState(
                    s => s.form.Common.FamilyPhysicianPrimaryCareProvider = attendant,
                    () => {
                        if (isSelected)
                            this.handleFormUpdate();
                    }
                ),
                null,
                "ObstetricalFile_FamilyPhysicianOrPrimaryCareProvider",
                OntarioObstetricalFileFieldLimits.Page1.FamilyPhysicianOrPrimaryCareProvider.MaxChars
            ),
            colClassName
        );
    }

    protected renderAllergiesOrSensitivites(colClassName: string): JSX.Element {
        return this.renderField(
            "ObstetricalFile_AllergiesOrSensitivitiesLabel",
            this.controlRenderer.renderResizableTextarea({
                value: this.state.form.Common.AllergiesOrSensitivities,
                onChange: value => this.mutateModelState(s => s.form.Common.AllergiesOrSensitivities = value),
                containerCssClass: "resizable",
                minHeight: "34px"
            }),
            colClassName
        );
    }

    protected renderMedications(colClassName: string): JSX.Element {
        return this.renderField(
            "ObstetricalFile_MedicationsLabel",
            this.controlRenderer.renderResizableTextarea({
                value: this.state.form.Common.Medications,
                onChange: value => this.mutateModelState(s => s.form.Common.Medications = value),
                containerCssClass: "resizable",
                minHeight: "34px"
            }),
            colClassName
        );
    }

    protected mutateModelStateWithFormUpdate(mutation: (state: TState) => void, then?: () => void): void {
        this.mutateState(
            s => {
                mutation(s);
            },
            () => {
                then?.();
                this.handleFormUpdate();
            }
        );
    }

    protected mutateModelState(mutation: (state: TState) => void, then?: () => void): void {
        this.mutateState(
            s => {
                mutation(s);
            },
            then
        );
    }

    protected renderSectionTitleRow(labelKey: string, className?: string): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12">
                    <h4 className={`text-bold ${className ?? ""}`}>{this.getString(labelKey)}</h4>
                </div>
            </div>
        );
    }

    protected getSubcomponentBaseProps(): BaseOntarioObstetricalFileSubSectionProps {
        return {
            contributionId: this.props.contributionId,
            readonly: this.props.readonly,
            previousObstetricalFile: this.props.previousObstetricalFile,
            handleFormUpdate: () => this.handleFormUpdate(),
            form: this.state.form,
            clickableTextId: this.state.currentClickableTextId,
            onClickableTextChange: (id: string) => this.mutateState(s => s.currentClickableTextId = id),
            mutateModelState: (mutation, then) => this.mutateModelState(s => mutation(s.form), then),
            mutateModelStateWithFormUpdate: (mutation, then) => this.mutateModelStateWithFormUpdate(s => mutation(s.form), then)
        };
    }

    protected addSubsequentVisitRow(props: IObstetricalFilePageProps<OntarioObstetricalFileForm>): void {
        if (props.contributionType === ObstetricalFileContributionType.Postnatal)
            return;

        this.mutateModelState(
            s => {
                const gestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(
                    s.form.Common.FinalEstimatedDateOfBirth,
                    props.encounter.ConsultationDate
                );

                const newSubsequentVisit = new OntarioObstetricalVisit({
                    ContributionId: props.contributionId,
                    Date: props.encounter.ConsultationDate,
                    GestationalAge: gestationalAge
                });

                const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                    obstetricalFileMedicalSummary: props.obstetricalFileMedicalSummary,
                    consultationDate: props.encounter.ConsultationDate,
                    obstetricalFileContributionDate: props.contributionCreationDate,
                    skipLoadLastHour: false,
                    weightUnit: props.form.Metadata.subsequentVisitsTableWeightUnit
                });

                newSubsequentVisit.vitalSign = vitalSign;
                newSubsequentVisit.EmrTemplateId = vitalSign?.emrTemplateId;

                s.form.Opr3.SubsequentVisits = [...s.form.Opr3.SubsequentVisits, newSubsequentVisit];
            },
            () => {
                const subsequentVisit = this.state.form.Opr3.SubsequentVisits.firstOrDefault(p => p.ContributionId === this.props.contributionId);

                if (subsequentVisit?.EmrTemplateId)
                    this.handleObstetricalFileVitalSignLink(subsequentVisit.EmrTemplateId);
                else if (subsequentVisit)
                    this.handleFormUpdate();
            }
        );
    }
}

export { OntarioObstetricalFilePage, OntarioObstetricalFilePageState };
